import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PublicIcon from '@mui/icons-material/Public';

import type { Visibility } from '../../domain';
import { useCollections, useCreateCollection, useFavorites, useToggleFavorite } from '../../queries';
import { ApiResponseError } from '../../api/envelope';
import { resolveAssetUrl } from './assetUtils';

interface Props {
  selectedId: string | null;
  onSelect: (collectionId: string) => void;
}

const VISIBILITY_ICON: Record<Visibility, React.ReactNode> = {
  Private: <LockOutlinedIcon sx={{ fontSize: 13 }} />,
  Shared: <PeopleOutlineIcon sx={{ fontSize: 13 }} />,
  Public: <PublicIcon sx={{ fontSize: 13 }} />,
};

/**
 * Left-rail collections navigator (Path C, Slice 7). Lists the user's
 * collections (newest first), each row carrying a cover thumbnail, asset
 * count, visibility chip, and a favorite heart. "New Collection" opens a
 * create dialog. Clicking a row selects it for the right pane.
 */
export const CollectionsPanel: React.FC<Props> = ({ selectedId, onSelect }) => {
  const { data: collections, isLoading, error } = useCollections();
  const { data: favCollections } = useFavorites('Collection');
  const toggleFavorite = useToggleFavorite();
  const createCollection = useCreateCollection();

  const favoritedIds = new Set((favCollections ?? []).map(f => f.targetId));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('Private');

  const resetDialog = () => {
    setName('');
    setDescription('');
    setVisibility('Private');
    setDialogOpen(false);
  };

  const handleCreate = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    createCollection.mutate(
      {
        labId: 'image-lab',
        name: trimmed,
        description: description.trim() || undefined,
        visibility,
      },
      {
        onSuccess: created => {
          resetDialog();
          onSelect(created.id);
        },
      },
    );
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
      aria-label="Collections"
    >
      <Box sx={{ p: 1.5, borderBottom: theme => `1px solid ${theme.palette.divider}` }}>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          New Collection
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={20} />
          </Stack>
        )}

        {error && (
          <Box sx={{ px: 1.5 }}>
            <Alert severity="error" sx={{ fontSize: 12 }}>
              {error instanceof ApiResponseError
                ? `${error.code}: ${error.message}`
                : error instanceof Error
                  ? error.message
                  : 'Failed to load collections.'}
            </Alert>
          </Box>
        )}

        {collections && collections.length === 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 1.5, display: 'block' }}
          >
            No collections yet. Create one to start grouping assets.
          </Typography>
        )}

        {(collections ?? []).map(c => {
          const isSelected = c.id === selectedId;
          const cover = resolveAssetUrl(c.coverThumbnailUrl);
          const isFavorited = favoritedIds.has(c.id);
          return (
            <Stack
              key={c.id}
              direction="row"
              spacing={1}
              alignItems="center"
              onClick={() => onSelect(c.id)}
              sx={{
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                bgcolor: isSelected ? 'action.selected' : 'transparent',
                borderLeft: theme =>
                  isSelected
                    ? `2px solid ${theme.palette.primary.main}`
                    : '2px solid transparent',
                '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 27, // 16:9
                  flexShrink: 0,
                  borderRadius: 0.5,
                  overflow: 'hidden',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {cover ? (
                  <Box
                    component="img"
                    src={cover}
                    alt=""
                    loading="lazy"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <CollectionsBookmarkIcon sx={{ fontSize: 16, opacity: 0.4 }} />
                )}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {c.name}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {c.assetCount} asset{c.assetCount === 1 ? '' : 's'}
                  </Typography>
                  <Chip
                    icon={VISIBILITY_ICON[c.visibility] as React.ReactElement}
                    label={c.visibility}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: 10 } }}
                  />
                </Stack>
              </Box>

              <Tooltip title={isFavorited ? 'Unfavorite' : 'Favorite'}>
                <IconButton
                  size="small"
                  aria-label={isFavorited ? 'Unfavorite collection' : 'Favorite collection'}
                  onClick={e => {
                    e.stopPropagation();
                    toggleFavorite.mutate({ targetType: 'Collection', targetId: c.id });
                  }}
                  sx={{ color: isFavorited ? 'error.main' : 'text.secondary' }}
                >
                  {isFavorited ? (
                    <FavoriteIcon fontSize="small" />
                  ) : (
                    <FavoriteBorderIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
          );
        })}
      </Box>

      <Dialog open={dialogOpen} onClose={resetDialog} maxWidth="xs" fullWidth>
        <DialogTitle>New Collection</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              autoFocus
              label="Name"
              size="small"
              fullWidth
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleCreate();
              }}
            />
            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="visibility-label">Visibility</InputLabel>
              <Select
                labelId="visibility-label"
                label="Visibility"
                value={visibility}
                onChange={e => setVisibility(e.target.value as Visibility)}
              >
                <MenuItem value="Private">Private</MenuItem>
                <MenuItem value="Shared">Shared</MenuItem>
                <MenuItem value="Public">Public</MenuItem>
              </Select>
            </FormControl>
            {createCollection.isError && (
              <Alert severity="error" sx={{ fontSize: 12 }}>
                {createCollection.error instanceof ApiResponseError
                  ? `${createCollection.error.code}: ${createCollection.error.message}`
                  : 'Failed to create collection.'}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!name.trim() || createCollection.isPending}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
